import React from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
interface ResearchSectionProps {
  data: {
    targetAudience: string[];
    competitors: Array<{
      name: string;
      description: string;
    }>;
  };
  isEditMode: boolean;
  onUpdate: (data: any) => void;
}
export const ResearchSection = ({
  data,
  isEditMode,
  onUpdate
}: ResearchSectionProps) => {
  const addAudience = () => {
    onUpdate({
      ...data,
      targetAudience: [...data.targetAudience, 'New audience segment']
    });
  };
  const removeAudience = (index: number) => {
    onUpdate({
      ...data,
      targetAudience: data.targetAudience.filter((_, i) => i !== index)
    });
  };
  const updateAudience = (index: number, value: string) => {
    const updated = [...data.targetAudience];
    updated[index] = value;
    onUpdate({
      ...data,
      targetAudience: updated
    });
  };
  const addCompetitor = () => {
    onUpdate({
      ...data,
      competitors: [...data.competitors, {
        name: 'New Competitor',
        description: 'Description'
      }]
    });
  };
  const removeCompetitor = (index: number) => {
    onUpdate({
      ...data,
      competitors: data.competitors.filter((_, i) => i !== index)
    });
  };
  const updateCompetitor = (index: number, field: string, value: string) => {
    const updated = [...data.competitors];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    onUpdate({
      ...data,
      competitors: updated
    });
  };
  return <section className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300" data-id="element-150">
      <div className="bg-gradient-to-r from-[#1A2332] to-[#2A3F5F] py-6 px-8" data-id="element-151">
        <h2 className="text-2xl font-bold text-white flex items-center" data-id="element-152">
          <span className="inline-block h-11 w-11 rounded-xl bg-white text-[#1A2332] flex items-center justify-center mr-4 text-lg font-bold shadow-md" data-id="element-153">
            2
          </span>
          Research
        </h2>
      </div>
      <div className="bg-white dark:bg-[#1A2332] p-10 space-y-10" data-id="element-154">
        <div data-id="element-155">
          <div className="flex items-center justify-between mb-5" data-id="element-156">
            <h3 className="text-xl font-semibold text-[#1A2332] dark:text-white flex items-center" data-id="element-157">
              <div className="h-2.5 w-2.5 rounded-full bg-[#7FE5A8] mr-3" data-id="element-158"></div>
              Target Audience
            </h3>
            {isEditMode && <button onClick={addAudience} className="flex items-center gap-2 px-3 py-1.5 bg-[#7FE5A8] text-[#1A2332] rounded-lg hover:bg-[#6FD598] transition-colors text-sm font-semibold" data-id="element-159">
                <PlusIcon className="h-4 w-4" data-id="element-160" />
                Add
              </button>}
          </div>
          <ul className="space-y-4 ml-6" data-id="element-161">
            {data.targetAudience.map((audience, index) => <li key={index} className="flex items-start text-gray-600 dark:text-gray-300 text-base leading-relaxed" data-id="element-162">
                <span className="inline-block h-2 w-2 rounded-full bg-[#1A2332] dark:bg-[#7FE5A8] mr-3 mt-2 flex-shrink-0" data-id="element-163"></span>
                {isEditMode ? <div className="flex-1 flex items-center gap-2" data-id="element-164">
                    <input type="text" value={audience} onChange={e => updateAudience(index, e.target.value)} className="flex-1 border-2 border-[#7FE5A8] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7FE5A8] dark:bg-[#2A3F5F] dark:text-white" data-id="element-165" />
                    <button onClick={() => removeAudience(index)} className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors" data-id="element-166">
                      <XIcon className="h-4 w-4" data-id="element-167" />
                    </button>
                  </div> : <span data-id="element-168">{audience}</span>}
              </li>)}
          </ul>
        </div>
        <div data-id="element-169">
          <div className="flex items-center justify-between mb-5" data-id="element-170">
            <h3 className="text-xl font-semibold text-[#1A2332] dark:text-white flex items-center" data-id="element-171">
              <div className="h-2.5 w-2.5 rounded-full bg-[#7FE5A8] mr-3" data-id="element-172"></div>
              Competitor Insights
            </h3>
            {isEditMode && <button onClick={addCompetitor} className="flex items-center gap-2 px-3 py-1.5 bg-[#7FE5A8] text-[#1A2332] rounded-lg hover:bg-[#6FD598] transition-colors text-sm font-semibold" data-id="element-173">
                <PlusIcon className="h-4 w-4" data-id="element-174" />
                Add
              </button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-175">
            {data.competitors.map((competitor, index) => <div key={index} className={`${competitor.name === 'Heliona' ? 'bg-gradient-to-br from-[#7FE5A8] to-[#5FD88F]' : 'bg-[#2A3F5F] dark:bg-[#344A66]'} rounded-xl p-6 hover:shadow-lg hover:scale-[1.02] transition-all duration-200`} data-id="element-176">
                {isEditMode ? <div className="space-y-3" data-id="element-177">
                    <div className="flex items-center gap-2" data-id="element-178">
                      <input type="text" value={competitor.name} onChange={e => updateCompetitor(index, 'name', e.target.value)} className="flex-1 border-2 border-[#7FE5A8] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7FE5A8] font-semibold text-lg dark:bg-[#1A2332] dark:text-white" data-id="element-179" />
                      <button onClick={() => removeCompetitor(index)} className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors" data-id="element-180">
                        <XIcon className="h-4 w-4" data-id="element-181" />
                      </button>
                    </div>
                    <textarea value={competitor.description} onChange={e => updateCompetitor(index, 'description', e.target.value)} className="w-full border-2 border-[#7FE5A8] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7FE5A8] dark:bg-[#1A2332] dark:text-white" rows={2} data-id="element-182" />
                  </div> : <>
                    <p className={`font-semibold text-lg mb-2 ${competitor.name === 'Heliona' ? 'text-[#1A2332]' : 'text-white'}`} data-id="element-183">
                      {competitor.name}
                    </p>
                    <p className={`leading-relaxed ${competitor.name === 'Heliona' ? 'text-[#1A2332] font-medium' : 'text-gray-300'}`} data-id="element-184">
                      {competitor.description}
                    </p>
                  </>}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};